import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  ReferenceType,
  ReferenceSource,
  ReferenceEntityType,
} from "@erp/shared-types";

const UUIDSchema = z.uuid();

/**
 * Shared schema for an external system reference record
 */
export const ExternalSystemReferenceSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  entityType: z.nativeEnum(ReferenceEntityType).nullable(),
  entityId: UUIDSchema.nullable(),
  referenceType: z.nativeEnum(ReferenceType),
  referenceValue: z.string(),
  source: z.nativeEnum(ReferenceSource),
  sourceSystem: z.string().nullable(),
  sourceId: z.string().nullable(),
  confidence: z.number().nullable(),
  rawText: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Input schema for getting a single reference by ID
 */
export const GetExternalSystemReferenceInputSchema = z.object({
  id: UUIDSchema,
});

export const GetExternalSystemReferenceResponseSchema = z.object({
  reference: ExternalSystemReferenceSchema,
});

/**
 * Input schema for listing references by entity
 */
export const ListExternalSystemReferencesByEntityInputSchema = z.object({
  entityType: z.nativeEnum(ReferenceEntityType),
  entityId: UUIDSchema,
});

export const ListExternalSystemReferencesResponseSchema = z.object({
  references: z.array(ExternalSystemReferenceSchema),
});

export const externalSystemReferencesContracts = {
  get: oc
    .input(GetExternalSystemReferenceInputSchema)
    .output(GetExternalSystemReferenceResponseSchema)
    .route({
      method: "GET",
      summary: "Get an external reference",
      description:
        "Returns one stored reference number (such as a PO or invoice number from another system), with its source, confidence and the record it belongs to.",
    }),
  listByEntity: oc
    .input(ListExternalSystemReferencesByEntityInputSchema)
    .output(ListExternalSystemReferencesResponseSchema)
    .route({
      method: "GET",
      summary: "List external references for a record",
      description:
        "Returns all stored reference numbers from other systems or documents attached to one record, with source and confidence.",
    }),
} as const;

export type ExternalSystemReferenceItem = z.infer<
  typeof ExternalSystemReferenceSchema
>;
export type GetExternalSystemReferenceInput = z.infer<
  typeof GetExternalSystemReferenceInputSchema
>;
export type GetExternalSystemReferenceResponse = z.infer<
  typeof GetExternalSystemReferenceResponseSchema
>;
export type ListExternalSystemReferencesByEntityInput = z.infer<
  typeof ListExternalSystemReferencesByEntityInputSchema
>;
export type ListExternalSystemReferencesResponse = z.infer<
  typeof ListExternalSystemReferencesResponseSchema
>;
